import { useState, useEffect, useRef } from 'react'
import { User, Users, GraduationCap, BookOpen, Building, MoreHorizontal } from 'lucide-react'
import { cn } from '@/utils/cn'

interface ProfileType {
  id: string
  title: string
  description: string
  icon: React.ElementType
}

interface ProfileTypeStepProps {
  value?: string
  onChange: (value: string) => void
  onValidationChange: (isValid: boolean) => void
}

const profileTypes: ProfileType[] = [
  {
    id: 'producer',
    title: 'Produtor(a) Rural',
    description: 'Agricultor, pecuarista ou responsável por propriedade rural',
    icon: User
  },
  {
    id: 'technician',
    title: 'Técnico(a) / Consultor(a)',
    description: 'Profissional que presta assistência técnica ou consultoria',
    icon: Users
  },
  {
    id: 'researcher',
    title: 'Pesquisador(a)',
    description: 'Cientista ou acadêmico que estuda agricultura regenerativa',
    icon: GraduationCap
  },
  {
    id: 'student',
    title: 'Estudante',
    description: 'Em formação na área de agronomia, veterinária ou áreas afins',
    icon: BookOpen
  },
  {
    id: 'institution',
    title: 'Representante de Instituição',
    description: 'Empresa, instituição de ensino, pesquisa ou governo',
    icon: Building
  },
  {
    id: 'other',
    title: 'Outro',
    description: 'Perfil diferente das opções acima',
    icon: MoreHorizontal
  }
]

export function ProfileTypeStep({ value, onChange, onValidationChange }: ProfileTypeStepProps) {
  const [selectedType, setSelectedType] = useState(value || '')
  const hasNotifiedValidation = useRef(false)

  // Validar estado inicial quando o componente monta
  useEffect(() => {
    if (!hasNotifiedValidation.current) {
      const isValid = !!selectedType
      onValidationChange(isValid)
      hasNotifiedValidation.current = true
    }
  }, [selectedType, onValidationChange])

  const handleSelect = (typeId: string) => {
    setSelectedType(typeId)
    onChange(typeId)
    onValidationChange(true) // Sempre válido se algo for selecionado
  }

  return (
    <div className="space-y-6">

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {profileTypes.map((type) => {
          const Icon = type.icon
          const isSelected = selectedType === type.id

          return (
            <button
              key={type.id}
              onClick={() => handleSelect(type.id)}
              className={cn(
                "p-6 rounded-xl border-2 transition-all duration-200 text-left",
                "hover:shadow-lg hover:scale-[1.02]",
                isSelected
                  ? "border-brand-primary bg-brand-primary/10 shadow-lg ring-2 ring-brand-primary/20"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    "w-12 h-12 rounded-lg flex items-center justify-center transition-colors",
                    isSelected
                      ? "bg-brand-primary text-white"
                      : "bg-brand-surface-alt text-brand-primary"
                  )}
                >
                  <Icon size={24} />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-brand-text mb-2">
                    {type.title}
                  </h3>
                  <p className="text-sm text-brand-text-secondary leading-relaxed">
                    {type.description}
                  </p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {selectedType && (
        <div className="mt-6 p-4 bg-brand-accent/10 rounded-xl border border-brand-accent/30">
          <p className="text-sm text-brand-text">
            <span className="font-medium">Perfil selecionado:</span> {profileTypes.find(t => t.id === selectedType)?.title}
          </p>
        </div>
      )}
    </div>
  )
}
